import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { actors } from "@/mocks/actors";
import { movies } from "@/mocks/movies";
import { useSeo } from "@/hooks/useSeo";
import type { Movie } from "@/types";

export default function ActorDetail() {
  const { id } = useParams<{ id: string }>();
  const actor = actors.find((item) => item.id === id);
  const filmography: Movie[] = actor
    ? movies.filter((movie) => actor.films.includes(movie.id))
    : [];
  const collaborators = actor
    ? actors.filter(
        (item) =>
          item.id !== actor.id &&
          item.films.some((filmId) => actor.films.includes(filmId)),
      )
    : [];

  useSeo({
    title: actor ? `${actor.name}演员资料与参演作品 - 蜜桃成熟时电影` : "演员未找到 - 蜜桃成熟时电影",
    description: actor
      ? `${actor.name}（${actor.englishName}）演员资料：个人简介、出生信息与在蜜桃成熟时系列中的参演作品列表。`
      : "未找到对应的演员资料，请返回演员列表重新浏览。",
    keywords: actor
      ? `${actor.name},${actor.englishName},演员资料,参演作品,蜜桃成熟时演员`
      : "演员资料,蜜桃成熟时电影",
  });

  if (!actor) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1240px] flex-col items-center gap-4 px-4 py-24 text-center md:px-6">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-user-unfollow-line text-2xl" />
          </span>
          <h1 className="font-heading text-2xl font-semibold text-foreground-950">未找到这位演员</h1>
          <p className="max-w-md text-sm leading-relaxed text-foreground-600">
            演员资料可能已调整或链接有误，可以回到演员列表浏览全部档案。
          </p>
          <Link
            to="/actors"
            className="mt-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回演员列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-4 md:px-6">
          <Breadcrumb
            items={[{ label: "首页", to: "/" }, { label: "演员资料", to: "/actors" }, { label: actor.name }]}
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
        <div className="grid gap-8 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-12">
          <aside>
            <div className="overflow-hidden rounded-lg border border-background-200/80 bg-background-50">
              <div className="aspect-square w-full overflow-hidden bg-background-200">
                <img
                  src={actor.avatar}
                  alt={`${actor.name}演员头像`}
                  title={`${actor.name}演员资料`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="p-5">
                <h1 className="font-heading text-xl font-semibold text-foreground-950">{actor.name}</h1>
                <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-foreground-400">
                  {actor.englishName}
                </p>
                <dl className="mt-4 space-y-2.5 text-xs">
                  <div className="flex items-center justify-between gap-3 border-b border-background-200/70 pb-2.5">
                    <dt className="text-foreground-400">出生年份</dt>
                    <dd className="text-foreground-800">{actor.birthYear}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-400">出生地</dt>
                    <dd className="text-foreground-800">{actor.birthplace}</dd>
                  </div>
                </dl>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="rounded-full bg-accent-100 px-2.5 py-1 text-[11px] text-accent-700">
                    系列演员
                  </span>
                  <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900">
                    参演 {filmography.length} 部
                  </span>
                </div>
              </div>
            </div>

            {collaborators.length > 0 && (
              <div className="mt-4 rounded-lg bg-background-100 p-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-950">合作演员</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {collaborators.map((item) => (
                    <Link
                      key={item.id}
                      to={`/actors/${item.id}`}
                      className="inline-flex items-center gap-2 rounded-full bg-background-50 px-2.5 py-1.5 text-[11px] text-foreground-700 transition-colors hover:text-accent-600"
                    >
                      <span className="h-5 w-5 overflow-hidden rounded-full bg-background-200">
                        <img src={item.avatar} alt={item.name} className="h-full w-full object-cover object-top" />
                      </span>
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>

          <div className="min-w-0">
            <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950">
              <span className="h-4 w-1 rounded-full bg-accent-500" />
              演员简介
            </h2>
            <div className="mt-4 space-y-4">
              {actor.bio.map((paragraph, index) => (
                <p key={index} className="text-sm leading-[1.95] text-foreground-700 md:text-[15px]">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-10">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-accent-500" />
                  参演作品
                </h2>
                <span className="text-xs text-foreground-400">{filmography.length} 部相关影片</span>
              </div>

              <div className="flex flex-col divide-y divide-background-200 border-y border-background-200">
                {filmography.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/detail/${movie.id}`}
                    className="group flex items-center gap-4 py-4"
                  >
                    <span className="h-[78px] w-[56px] shrink-0 overflow-hidden rounded-sm bg-background-200">
                      <img
                        src={movie.cover}
                        alt={movie.title}
                        title={`${movie.title}剧情介绍`}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-heading text-sm font-semibold text-foreground-900 transition-colors group-hover:text-accent-600">
                          {movie.title}
                        </span>
                        <span className="rounded-full bg-background-100 px-2 py-0.5 text-[10px] text-foreground-500">
                          {movie.year}
                        </span>
                      </span>
                      <span className="mt-1 block text-[11px] text-foreground-400">
                        {movie.director} 导演 · {movie.genres.join(" / ")}
                      </span>
                      <span className="mt-1.5 line-clamp-1 text-xs text-foreground-500">{movie.summary}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-xs text-accent-600">
                      <i className="ri-star-fill text-[10px]" />
                      {movie.rating.toFixed(1)}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-6">
              <Link
                to="/actors"
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-foreground-600 transition-colors hover:text-accent-600"
              >
                <i className="ri-arrow-left-line" />
                返回演员列表
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-foreground-600 transition-colors hover:text-accent-600"
              >
                浏览全部影片
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}